import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, CheckCircle2, AlertTriangle, RefreshCw, Camera, Sparkles, ChevronRight, ShieldCheck, BookOpen, Languages, Grid3x3, FileText, Sigma, FlaskConical, ListChecks, BookMarked, Tag, Lightbulb } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'sip_token';
const POLL_MS = 2000;
const POLL_TIMEOUT_MS = 90 * 1000;

const PROGRESS_STAGES = [
  'Reading pages…',
  'Detecting chapter…',
  'Understanding diagrams…',
  'Understanding tables…',
  'Understanding activities…',
  'Understanding exercises…',
  'Extracting keywords…',
  'Preparing educational understanding…',
  'Building content inventory…',
];

const FIELD_META = {
  chapterTitle:      { label: 'Chapter title',      icon: BookOpen },
  detectedLanguages: { label: 'Detected languages', icon: Languages },
  pagesCaptured:     { label: 'Pages captured',     icon: Grid3x3 },
  diagramsFound:     { label: 'Diagrams',           icon: Sparkles },
  tablesFound:       { label: 'Tables',             icon: FileText },
  formulaeFound:     { label: 'Formulae',           icon: Sigma },
  activitiesFound:   { label: 'Activities',         icon: FlaskConical },
  exercisesFound:    { label: 'Exercises',          icon: ListChecks },
  definitionsFound:  { label: 'Definitions',        icon: BookMarked },
  keywords:          { label: 'Keywords',           icon: Tag },
  importantConcepts: { label: 'Important concepts', icon: Lightbulb },
};

export default function BoscoCaptureUnderstandingView() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: 'polling' }); // 'polling'|'ready'|'not-educational'|'unsafe'|'session-unhealthy'|'failed'
  const [stageIdx, setStageIdx] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [hints, setHints] = useState({ board: '', class: '', subject: '', medium: '' });

  // Rotating progress copy so the user sees continuous motion.
  useEffect(() => {
    if (state.status !== 'polling') return undefined;
    const t = setInterval(() => setStageIdx((i) => (i + 1) % PROGRESS_STAGES.length), 1400);
    return () => clearInterval(t);
  }, [state.status]);

  // Poll until we get a terminal status.
  useEffect(() => {
    if (!sessionId) return undefined;
    let cancelled = false;
    const deadline = Date.now() + POLL_TIMEOUT_MS;
    (async () => {
      const token = localStorage.getItem(TOKEN_KEY) || '';
      while (!cancelled && Date.now() < deadline) {
        try {
          const r = await fetch(`${API_URL}/api/bosco-capture/session/${sessionId}/understanding`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (r.ok) {
            const doc = await r.json();
            if (doc.status && doc.status !== 'processing') {
              setState(doc);
              return;
            }
          }
        } catch { /* keep polling */ }
        await new Promise((res) => setTimeout(res, POLL_MS));
      }
      if (!cancelled) setState({ status: 'failed', message: 'Understanding is taking longer than expected. Please try again.' });
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  // ── Terminal renders ──
  if (state.status === 'polling' || state.status === 'processing') {
    return <ProgressScreen text={PROGRESS_STAGES[stageIdx]} />;
  }
  if (state.status === 'not-educational') {
    return (
      <NotEducationalScreen
        reason={state.educationalReason}
        onRecapture={() => navigate('/bosco-capture')}
        onCancel={() => navigate('/teacher-assignments')}
      />
    );
  }
  if (state.status === 'unsafe') {
    return (
      <UnsafeScreen
        reason={state.moderation?.reason || 'Content was flagged by the safety layer.'}
        onCancel={() => navigate('/teacher-assignments')}
      />
    );
  }
  if (state.status === 'session-unhealthy') {
    return (
      <SessionUnhealthyScreen
        health={state.health}
        onRecapture={() => navigate('/bosco-capture')}
        onCancel={() => navigate('/teacher-assignments')}
      />
    );
  }
  if (state.status === 'failed') {
    return (
      <FailedScreen
        message={state.message || 'BOSCO could not process this capture.'}
        onRetry={() => navigate('/bosco-capture')}
        onCancel={() => navigate('/teacher-assignments')}
      />
    );
  }

  // ready
  const fields = state.fields || {};

  const proceed = async () => {
    // Phase 4 — hand off to kit picker.
    navigate(`/bosco-capture/kit/${sessionId}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-950 to-emerald-950/40 text-slate-100 pb-8"
         data-testid="bosco-capture-understanding">
      <div className="max-w-md mx-auto px-5 pt-6">
        <div className="flex items-center gap-2 mb-1 text-emerald-300 text-[10px] uppercase tracking-[0.2em] font-semibold">
          <Sparkles className="h-3 w-3" /> What BOSCO Understood
        </div>
        <h1 className="text-2xl font-bold">Content Inventory</h1>
        <p className="text-xs text-slate-400 mt-1">
          Only the details BOSCO is confident about are shown. Review and confirm.
        </p>

        {/* v1.1 — Session Health™ "weak" warning banner. Only rendered when the
            pre-Understanding aggregate flagged the session as borderline but
            not bad enough to reject outright. */}
        {state.health?.verdict === 'weak' && (
          <div className="mt-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-[11px] text-amber-200 flex items-start gap-2"
               data-testid="bosco-capture-weak-session-banner">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div>
              <span className="font-semibold">Capture Session Health™ — weak.</span>{' '}
              {state.health.message}
            </div>
          </div>
        )}

        {/* Content Inventory */}
        <div className="mt-5 space-y-2" data-testid="bosco-capture-inventory">
          {Object.keys(FIELD_META).map((k) => {
            const entry = fields[k];
            if (!entry) return null;
            const meta = FIELD_META[k];
            const Icon = meta.icon;
            const value = Array.isArray(entry.value) ? entry.value.join(', ') : String(entry.value);
            return (
              <div key={k} className="flex items-start gap-3 p-3 rounded-lg bg-white/5 border border-white/10 backdrop-blur"
                   data-testid={`bosco-capture-field-${k}`}>
                <Icon className="h-4 w-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{meta.label}</div>
                  <div className="text-sm mt-0.5 leading-snug break-words">{value}</div>
                </div>
                <span className="text-[10px] text-slate-500 font-mono flex-shrink-0 mt-1">
                  {Math.round(entry.confidence * 100)}%
                </span>
              </div>
            );
          })}
          {Object.keys(fields).length === 0 && (
            <p className="text-sm text-slate-400 italic py-6 text-center">
              BOSCO was not confident about any specific field. Add the details below or recapture.
            </p>
          )}
        </div>

        {/* Optional teacher inputs */}
        {editMode ? (
          <div className="mt-6 space-y-3" data-testid="bosco-capture-hints-form">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              Add optional context
            </div>
            {[
              ['board', 'Board (CBSE, ICSE, IB, …)'],
              ['class', 'Class (e.g. 10, XII)'],
              ['subject', 'Subject'],
              ['medium', 'Medium (English, Hindi, …)'],
            ].map(([k, label]) => (
              <div key={k}>
                <Label className="text-xs text-slate-400">{label}</Label>
                <Input
                  value={hints[k]}
                  onChange={(e) => setHints((h) => ({ ...h, [k]: e.target.value }))}
                  className="mt-1 bg-white/5 border-white/10 text-slate-100"
                  data-testid={`bosco-capture-hint-${k}`}
                />
              </div>
            ))}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setEditMode(true)}
            className="mt-4 w-full text-xs text-emerald-300 py-2 rounded-lg border border-white/10 hover:bg-white/5"
            data-testid="bosco-capture-open-edit"
          >
            Edit information · add board / class / subject
          </button>
        )}

        <div className="mt-6 grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            onClick={() => navigate('/bosco-capture')}
            data-testid="bosco-capture-recapture"
            className="bg-transparent border-white/20 text-slate-200 hover:bg-white/10"
          >
            <RefreshCw className="h-4 w-4 mr-1" /> Recapture
          </Button>
          <Button
            onClick={proceed}
            data-testid="bosco-capture-looks-correct"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <CheckCircle2 className="h-4 w-4 mr-1" /> Looks correct
          </Button>
        </div>

        <p className="mt-5 text-[10px] text-slate-500 text-center flex items-center justify-center gap-1">
          <ShieldCheck className="h-3 w-3" />
          Captured media has been deleted. Only your generated resources are kept.
        </p>
      </div>
    </div>
  );
}


// ─── Sub-screens ─────────────────────────────────────────────────────────────

function ProgressScreen({ text }) {
  return (
    <div className="fixed inset-0 bg-slate-950 text-slate-100 flex items-center justify-center px-6"
         data-testid="bosco-capture-progress">
      <div className="max-w-xs text-center">
        <div className="relative inline-flex items-center justify-center h-20 w-20">
          <div className="absolute inset-0 rounded-full bg-emerald-500/20 animate-ping" />
          <div className="relative h-20 w-20 rounded-full bg-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-600/40">
            <Sparkles className="h-9 w-9 text-white" />
          </div>
        </div>
        <div className="mt-5 text-[10px] uppercase tracking-[0.25em] text-emerald-400 font-semibold">
          BOSCO is Understanding
        </div>
        <div className="mt-2 text-base font-medium min-h-[24px]" data-testid="bosco-capture-progress-text">
          {text}
        </div>
        <div className="mt-3 text-[11px] text-slate-500 leading-relaxed">
          Reading educational material · analysing structure · extracting keywords…
        </div>
      </div>
    </div>
  );
}

function NotEducationalScreen({ reason, onRecapture, onCancel }) {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-6"
         data-testid="bosco-capture-not-educational">
      <div className="max-w-sm text-center">
        <div className="inline-flex h-14 w-14 rounded-2xl bg-amber-500/20 text-amber-300 items-center justify-center mb-4">
          <Camera className="h-7 w-7" />
        </div>
        <h2 className="text-lg font-semibold">This doesn&apos;t look like educational material</h2>
        <p className="text-sm text-slate-400 mt-2 leading-relaxed">{reason}</p>
        <p className="text-xs text-slate-500 mt-3">
          BOSCO Capture is optimised for textbooks, worksheets, notes, question banks, and similar
          study material. Please point your camera at a printed page and try again.
        </p>
        <div className="mt-6 grid grid-cols-2 gap-3">
          <Button variant="outline" onClick={onCancel} className="bg-transparent border-white/20 text-slate-200 hover:bg-white/10">Back</Button>
          <Button onClick={onRecapture} className="bg-emerald-600 hover:bg-emerald-700">
            Capture again <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function UnsafeScreen({ reason, onCancel }) {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-6"
         data-testid="bosco-capture-unsafe">
      <div className="max-w-sm text-center">
        <div className="inline-flex h-14 w-14 rounded-2xl bg-rose-500/20 text-rose-300 items-center justify-center mb-4">
          <ShieldCheck className="h-7 w-7" />
        </div>
        <h2 className="text-lg font-semibold">Content flagged by BOSCO Safety</h2>
        <p className="text-sm text-slate-400 mt-2 leading-relaxed">
          Your captured material was flagged and cannot be processed:
        </p>
        <p className="text-xs text-slate-500 mt-2 italic">{reason}</p>
        <div className="mt-6">
          <Button onClick={onCancel} className="bg-emerald-600 hover:bg-emerald-700 w-full">
            Back to assignments
          </Button>
        </div>
      </div>
    </div>
  );
}

function FailedScreen({ message, onRetry, onCancel }) {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-6"
         data-testid="bosco-capture-failed">
      <div className="max-w-sm text-center">
        <div className="inline-flex h-14 w-14 rounded-2xl bg-rose-500/20 text-rose-300 items-center justify-center mb-4">
          <AlertTriangle className="h-7 w-7" />
        </div>
        <h2 className="text-lg font-semibold">BOSCO could not process this capture</h2>
        <p className="text-sm text-slate-400 mt-2 leading-relaxed">{message}</p>
        <div className="mt-6 grid grid-cols-2 gap-3">
          <Button variant="outline" onClick={onCancel} className="bg-transparent border-white/20 text-slate-200 hover:bg-white/10">Cancel</Button>
          <Button onClick={onRetry} className="bg-emerald-600 hover:bg-emerald-700">
            Recapture
          </Button>
        </div>
      </div>
    </div>
  );
}


// v1.1 — Capture Session Health™ terminal state. Shown when the frame-quality
// aggregation on the backend rejects the session BEFORE running Gemini (e.g.
// hand covered the page in most frames, or the lens was blocked).
function SessionUnhealthyScreen({ health, onRecapture, onCancel }) {
  const h = health || {};
  const hindrancePct = Math.round(((h.hindranceRate || 0) * 100));
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-6"
         data-testid="bosco-capture-session-unhealthy">
      <div className="max-w-sm text-center">
        <div className="inline-flex h-14 w-14 rounded-2xl bg-amber-500/20 text-amber-300 items-center justify-center mb-4">
          <AlertTriangle className="h-7 w-7" />
        </div>
        <h2 className="text-lg font-semibold">Capture Session Health™ — recapture needed</h2>
        <p className="text-sm text-slate-400 mt-2 leading-relaxed">{h.message || 'Some frames were blocked or too blurry to process.'}</p>
        <div className="mt-4 rounded-lg border border-white/10 bg-white/5 p-3 text-[11px] text-slate-400 text-left"
             data-testid="bosco-capture-session-health-summary">
          <div className="flex justify-between"><span>Frames captured</span><span>{h.totalFrames ?? '—'}</span></div>
          <div className="flex justify-between"><span>Sharp & unobstructed</span><span>{h.healthyFrames ?? '—'}</span></div>
          <div className="flex justify-between"><span>Blocked by hand / lens</span><span>{hindrancePct}%</span></div>
          <div className="flex justify-between"><span>Mean quality score</span><span>{h.meanScore ?? '—'} / 100</span></div>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-3">
          <Button variant="outline" onClick={onCancel} className="bg-transparent border-white/20 text-slate-200 hover:bg-white/10" data-testid="bosco-capture-session-unhealthy-cancel">Back</Button>
          <Button onClick={onRecapture} className="bg-emerald-600 hover:bg-emerald-700" data-testid="bosco-capture-session-unhealthy-recapture">
            Recapture
          </Button>
        </div>
      </div>
    </div>
  );
}
